import PrintResultButton from "@/components/PrintResultButton";
import PrintCoverSheet from "@/components/PrintCoverSheet";
import ToolServiceBacklinks from "@/components/ToolServiceBacklinks";
import { CASE_SERVICE_LINKS, ROUTE_SERVICE_LINKS, CORE_SERVICE_LINKS } from "@/data/tool-service-links";
// /tools/document-advisor — ตัวช่วยเลือกชุดเอกสารตามประเภทเคส
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { CheckCircle2, FileText, ArrowRight, Info } from "lucide-react";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { organizationSchema, speakableSchema, faqPageSchema, howToSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import {
  CASE_PRESETS,
  DESTINATIONS,
  ROUTE_LABELS,
  type CasePreset,
} from "@/data/service-planner";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/tools/document-advisor";
const TITLE = "ตัวช่วยเลือกชุดเอกสาร — บอกประเภทเคส รู้ทันทีว่าต้องเตรียมอะไร | NYC Legal";
const DESC =
  "เลือกวัตถุประสงค์และประเทศปลายทาง ระบบจะสรุปรายการเอกสารที่ต้องเตรียม ลำดับการรับรอง และข้อควรระวังที่ทำให้เอกสารถูกตีกลับบ่อยที่สุด";

const FAQ = [
  {
    q: "ตัวช่วยเลือกชุดเอกสารนี้ใช้งานอย่างไร",
    a: "เลือกวัตถุประสงค์ของคุณ เช่น เรียนต่อ ทำงาน สมรส ย้ายถิ่นฐาน หรือเอกสารบริษัท จากนั้นเลือกประเทศปลายทาง ระบบจะแสดงรายการเอกสารต้นทางที่ต้องเตรียม ลำดับขั้นการรับรอง (แปลรับรอง → กรมการกงสุล → Apostille หรือสถานทูต) และข้อควรระวังเฉพาะเคสนั้น รายการนี้เป็นแนวทางเริ่มต้น ควรตรวจสอบเช็กลิสต์ล่าสุดของหน่วยงานปลายทางประกอบเสมอ",
  },
  {
    q: "จะรู้ได้อย่างไรว่าเอกสารของฉันต้อง Apostille หรือต้องผ่านสถานทูต",
    a: "ขึ้นกับว่าประเทศปลายทางเป็นภาคีอนุสัญญา Apostille หรือไม่ ถ้าเป็นภาคี เอกสารที่ผ่านการรับรองจากกรมการกงสุลแล้วขอ Apostille เพิ่มก็ใช้ได้เลย ไม่ต้องผ่านสถานทูต แต่ถ้าปลายทางไม่ใช่ภาคี ต้องนำเอกสารที่กรมการกงสุลรับรองแล้วไปให้สถานทูตหรือสถานกงสุลของประเทศนั้นในไทยรับรองต่ออีกชั้น",
  },
  {
    q: "เอกสารเอกชน เช่น หนังสือมอบอำนาจ ต้องทำอะไรเพิ่มจากเอกสารราชการ",
    a: "เอกสารเอกชนที่ไม่มีตราราชการ เช่น หนังสือมอบอำนาจ คำให้การ (affidavit) หรือสัญญา ต้องให้ผู้ลงนามลงลายมือชื่อต่อหน้าทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ที่ขึ้นทะเบียนกับสภาทนายความฯ ก่อน แล้วจึงนำเข้าสู่ขั้นตอนแปลรับรองและกรมการกงสุลตามลำดับ",
  },
  {
    q: "เอกสารมีอายุใช้งานนานแค่ไหน",
    a: "หน่วยงานส่วนใหญ่กำหนดให้เอกสารสถานะบุคคล เช่น หนังสือรับรองความเป็นโสด ทะเบียนบ้าน หรือหนังสือรับรองบริษัท มีอายุไม่เกิน 3–6 เดือนนับจากวันออก จึงควรขอเอกสารใกล้กับวันยื่นจริง และตรวจสอบข้อกำหนดของหน่วยงานปลายทางก่อนเริ่มกระบวนการรับรอง",
  },
  {
    q: "สาเหตุที่เอกสารถูกตีกลับบ่อยที่สุดคืออะไร",
    a: "สาเหตุที่พบบ่อยได้แก่ การสะกดชื่อ-นามสกุลไม่ตรงกับหนังสือเดินทาง คำแปลชื่อสถานที่หรือหน่วยงานไม่สอดคล้องกันระหว่างเอกสารแต่ละฉบับ สำเนาไม่ชัดหรือตราประทับขาด เอกสารเกินอายุที่กำหนด และการข้ามลำดับขั้นการรับรอง เช่น ยื่นสถานทูตก่อนผ่านกรมการกงสุล",
  },
];

export const Route = createFileRoute("/tools/document-advisor")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "th") },
      { name: "description", content: ctaDesc(DESC, PATH, "th") },
      {
        name: "keywords",
        content:
          "ต้องเตรียมเอกสารอะไรบ้าง, เช็คลิสต์เอกสารวีซ่า, เอกสารจดทะเบียนสมรสต่างประเทศ, เอกสารเรียนต่อ, apostille หรือสถานทูต, รับรองเอกสารกงสุล",
      },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "th") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "th") },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "th_TH" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: canonicalAndHreflang(PATH),
    scripts: [
      consolidateGraph([
        organizationSchema(),
        speakableSchema([".aeo-speakable", "h1", "h2"]),
        faqPageSchema(FAQ),
        howToSchema({
          name: "วิธีเลือกชุดเอกสารให้ครบก่อนยื่น",
          description: "4 ขั้นตอนในการระบุเอกสารและลำดับการรับรองที่ถูกต้องตามประเทศปลายทาง",
          inLanguage: "th",
          steps: [
            { name: "ระบุวัตถุประสงค์", text: "เลือกว่าใช้เอกสารเพื่อเรียนต่อ ทำงาน สมรส ย้ายถิ่นฐาน ธุรกิจ หรือคดีความ" },
            { name: "ระบุประเทศปลายทาง", text: "ตรวจว่าปลายทางเป็นภาคี Apostille หรือต้องผ่านสถานทูตในไทย" },
            { name: "รวบรวมเอกสารต้นทาง", text: "ขอเอกสารจากสำนักทะเบียน สถาบันการศึกษา หรือหน่วยงานที่ออกเอกสาร" },
            { name: "เดินขั้นตอนรับรองตามลำดับ", text: "แปลรับรอง → กรมการกงสุล → Apostille หรือสถานทูตปลายทาง" },
          ],
        }),
      ]),
    ],
  }),
  component: DocumentAdvisorPage,
});

function DocumentAdvisorPage() {
  const [caseId, setCaseId] = useState<string>(CASE_PRESETS[0].id);
  const [dest, setDest] = useState<string>("us");

  const preset = useMemo<CasePreset>(
    () => CASE_PRESETS.find((c) => c.id === caseId) ?? CASE_PRESETS[0],
    [caseId],
  );
  const destination = useMemo(
    () => DESTINATIONS.find((d) => d.code === dest) ?? DESTINATIONS[0],
    [dest],
  );

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/" className="hover:underline">
          หน้าแรก
        </Link>{" "}
        / <Link to="/tools" className="hover:underline">เครื่องมือช่วยตัดสินใจ</Link> / ตัวช่วยเลือกชุดเอกสาร
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
        ตัวช่วยเลือกชุดเอกสารตามประเภทเคส
      </h1>
      <p className="aeo-speakable mt-3 text-base leading-relaxed text-muted-foreground">
        เลือกวัตถุประสงค์และประเทศปลายทางเพียง 2 ช่อง ระบบจะสรุปรายการเอกสารที่ต้องเตรียม
        ลำดับขั้นการรับรอง และข้อผิดพลาดที่ทำให้เอกสารถูกตีกลับบ่อยที่สุด
        รายการนี้เป็นแนวทางเริ่มต้น ควรตรวจสอบเช็กลิสต์ล่าสุดของหน่วยงานปลายทางประกอบเสมอ
      </p>

      <section className="mt-8 grid gap-4 rounded-xl border border-border bg-card p-5 md:grid-cols-2" data-print-hide>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">1. คุณใช้เอกสารเพื่ออะไร</span>
          <select
            value={caseId}
            onChange={(e) => setCaseId(e.target.value)}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
            aria-label="เลือกประเภทเคส"
          >
            {CASE_PRESETS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">2. ประเทศปลายทาง</span>
          <select
            value={dest}
            onChange={(e) => setDest(e.target.value)}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
            aria-label="เลือกประเทศปลายทาง"
          >
            {DESTINATIONS.map((d) => (
              <option key={d.code} value={d.code}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
      </section>

      <div className="mt-6 flex flex-wrap gap-3" data-print-hide>
        <PrintResultButton
          label="ดาวน์โหลดเช็กลิสต์เป็น PDF"
          fileName={`NYC-เช็กลิสต์เอกสาร-${preset.label}-${destination.name}`}
          fileNameEn={`NYC-Document-Checklist-${preset.id}-${dest}`}
        />
      </div>

      <PrintCoverSheet
        title="เช็กลิสต์เอกสารและลำดับการรับรอง"
        purpose={preset.label}
        destination={destination.name}
        routeLabel={ROUTE_LABELS[destination.route]}
        checklist={preset.documents}
        summary={`ชุดเอกสารสำหรับ ${preset.label} ปลายทาง ${destination.name} · รวม ${preset.documents.length} รายการ · ${preset.chain.length} ขั้นตอนการรับรอง`}
      />
      <section className="mt-8">
        <h2 className="text-xl font-semibold">เอกสารที่ต้องเตรียม — {preset.label}</h2>
        <ul className="mt-3 space-y-2">
          {preset.documents.map((d) => (
            <li key={d} className="flex gap-2 text-sm leading-relaxed">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
              <span>{d}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-8">
        <h2 className="text-xl font-semibold">ลำดับขั้นการรับรอง</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          เส้นทางสำหรับ {destination.name}: <strong>{ROUTE_LABELS[destination.route]}</strong>
        </p>
        <ol className="mt-3 space-y-2">
          {preset.chain.map((step, i) => (
            <li key={step} className="flex gap-3 rounded-lg border border-border bg-card p-3 text-sm">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                {i + 1}
              </span>
              <span className="leading-relaxed">{step}</span>
            </li>
          ))}
          {destination.route === "naati" && (
            <li className="flex gap-3 rounded-lg border border-border bg-card p-3 text-sm">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                {preset.chain.length + 1}
              </span>
              <span className="leading-relaxed">
                สำหรับออสเตรเลีย เอกสารที่ยื่น Department of Home Affairs หรือมหาวิทยาลัย
                ต้องแปลโดยนักแปล NAATI พร้อมตราประทับและหมายเลข CPN
              </span>
            </li>
          )}
        </ol>
      </section>

      <section className="mt-8 rounded-xl border border-border bg-muted/40 p-5">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Info className="h-5 w-5 text-primary" aria-hidden /> ข้อควรระวัง
        </h2>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-relaxed">
          {preset.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
          <li>
            สะกดชื่อ-นามสกุลในคำแปลทุกฉบับต้องตรงกับหนังสือเดินทาง
            และใช้คำแปลชื่อหน่วยงาน/สถานที่แบบเดียวกันทั้งชุด
          </li>
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-semibold">คำถามที่พบบ่อย</h2>
        <div className="mt-3 space-y-4">
          {FAQ.map((f) => (
            <details key={f.q} className="rounded-lg border border-border bg-card p-4">
              <summary className="cursor-pointer text-sm font-medium">{f.q}</summary>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <ToolServiceBacklinks
        heading={`บริการที่เกี่ยวข้องกับเคส “${preset.label}” (ปลายทาง ${destination.name})`}
        lead="เลือกหน้าบริการที่ตรงกับขั้นตอนของคุณ เพื่อดูรายละเอียดเอกสาร ขั้นตอน และข้อควรระวังเฉพาะงานนั้น"
        links={[
          ...(CASE_SERVICE_LINKS[preset.id] ?? []),
          ...(ROUTE_SERVICE_LINKS[destination.route] ?? []),
          ...CORE_SERVICE_LINKS,
        ]}
      />

      <section className="mt-10 flex flex-wrap gap-3">
        <Link
          to="/tools/timeline"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
        >
          ประเมินระยะเวลาแต่ละขั้น <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
        <Link
          to="/compare/service-types"
          className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-semibold"
        >
          <FileText className="h-4 w-4" aria-hidden /> เทียบ Notary / แปลรับรอง / NAATI / MFA
        </Link>
      </section>
    </main>
  );
}
